import { useEffect, useMemo, useRef } from 'react';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  clearQueuedPrompt,
  QueuedPrompt,
  setQueuedPrompt,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  clearPendingAnalysis,
  markTargetAnalysisFired,
  selectBoosterPendingAnalysisNames,
  selectBoosterTargetAnalysisFired,
  shiftPendingAnalysis,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import {
  ReportSchool,
  SchoolSelection,
  SchoolStanding,
  TargetSchoolFacts,
  TargetTest,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { deriveStanding } from '../utils/schoolContext';

const STRINGS = appStrings.modules.satActBooster;

const PROMPT_BUTTON_ID = 'booster_target_school_analysis';

/** The queue holds one prompt for the whole app, so ours has to be identified before acting. */
const isOurPrompt = (prompt: QueuedPrompt | null, timestamp: number) =>
  prompt?.promptButtonId === PROMPT_BUTTON_ID && prompt.timestamp === timestamp;

/** The name is carried so a prompt can be matched against the facts registered to answer it. */
type InFlightPrompt = Readonly<{ timestamp: number; schoolName: string }>;

type UseTargetSchoolAnalysisArgs = Readonly<{
  schools: readonly SchoolSelection[];
  /** The step's school-fit read, keyed by institution; the analysis adds no read of its own. */
  fitByInstitutionId: ReadonlyMap<number | null, ReportSchool>;
  /** True while a fit read that could still cover a newly added school is outstanding. */
  isFitPending: boolean;
  /** True when the fit read failed, so it yielded no rows. */
  hasFitError: boolean;
  test: TargetTest | null;
  currentScore: number | null;
  targetScore: number | null;
  recommendedGoal: number | null;
}>;

/**
 * Sends one analysis per added school, draining the slice's queue.
 *
 * Adds are queued rather than sent on the spot: one prompt can be queued at a time and it is taken
 * between turns, so dispatching per add would overwrite fires not yet picked up. A just-added
 * school also has no tier until the fit read covering it resolves.
 */
export const useTargetSchoolAnalysis = ({
  schools,
  fitByInstitutionId,
  isFitPending,
  hasFitError,
  test,
  currentScore,
  targetScore,
  recommendedGoal,
}: UseTargetSchoolAnalysisArgs) => {
  const dispatch = useAppDispatch();
  const hasFired = useAppSelector(selectBoosterTargetAnalysisFired);
  const pendingNames = useAppSelector(selectBoosterPendingAnalysisNames);
  const queuedPrompt = useAppSelector((state) => state.sessionState.queuedPrompt);

  const inFlightRef = useRef<InFlightPrompt | null>(null);
  const queuedPromptRef = useRef<QueuedPrompt | null>(null);

  // Anything queued before this mount lost its drain. Route exits never touch step
  // state, so clearing on the way in is the only place that catches both.
  useEffect(() => {
    dispatch(clearPendingAnalysis());
  }, [dispatch]);

  // Null while there is nothing to fire for, or the added school's fit row may still arrive.
  const facts = useMemo<TargetSchoolFacts | null>(() => {
    const addedName = pendingNames[0];

    if (addedName === undefined || test === null || currentScore === null) return null;

    const added = schools.find((school) => school.name === addedName);

    if (added === undefined) return null;

    const isFitMissing =
      added.institutionId !== null && !fitByInstitutionId.has(added.institutionId);

    // A linked school waits for its fit row, but only until the read settles: holding for a row
    // that never arrives would block every later add behind it.
    if (isFitMissing && isFitPending) return null;

    const goalScore = targetScore ?? recommendedGoal;
    const fit = added.institutionId === null ? null : fitByInstitutionId.get(added.institutionId);
    const mid50 = (test === 'act' ? fit?.actMid50 : fit?.satMid50) ?? null;
    const standingNow = mid50 === null ? null : deriveStanding(currentScore, mid50);
    const standingAtGoal =
      mid50 === null || goalScore === null ? null : deriveStanding(goalScore, mid50);
    // Supplied so the narration never subtracts; only a score short of the range has a gap.
    const pointsToLow = (standing: SchoolStanding | null, score: number | null) =>
      standing === 'below' && mid50 !== null && score !== null ? mid50.low - score : null;

    return {
      test,
      currentScore,
      goalScore,
      goalIsRecommended: targetScore === null && recommendedGoal !== null,
      isFirstAnalysis: !hasFired,
      // An unlinked school has no row to miss, so only a failed read leaves it unknown.
      fitUnavailable: isFitMissing && hasFitError,
      school: {
        name: added.name,
        tier: fit?.tier ?? null,
        mid50,
        siteUrl: fit?.siteUrl ?? null,
        standingNow,
        standingAtGoal,
        pointsToLowNow: pointsToLow(standingNow, currentScore),
        pointsToLowAtGoal: pointsToLow(standingAtGoal, goalScore),
        goalChangesStanding:
          standingNow !== null && standingAtGoal !== null && standingAtGoal !== standingNow,
      },
    };
  }, [
    pendingNames,
    schools,
    fitByInstitutionId,
    isFitPending,
    hasFitError,
    test,
    currentScore,
    targetScore,
    recommendedGoal,
    hasFired,
  ]);

  // Left registered until the prompt is taken, so the turn is answered by its own school's facts.
  useRegisterAgentContext(
    facts
      ? {
          kind: 'entity',
          description:
            'Computed facts for the one target school this analysis covers. These numbers, tiers and standings are authoritative — present them verbatim and never compare scores or recompute a range.',
          data: { targetSchoolFacts: facts },
        }
      : null,
  );

  useEffect(() => {
    queuedPromptRef.current = queuedPrompt;

    const inFlight = inFlightRef.current;

    if (inFlight !== null) {
      if (isOurPrompt(queuedPrompt, inFlight.timestamp)) {
        // The prompt names a school the registered facts no longer describe. Dropping it frees
        // the slot without spending a queue entry; the current school re-queues below.
        if (facts === null || facts.school.name !== inFlight.schoolName) {
          inFlightRef.current = null;
          dispatch(clearQueuedPrompt());
        }

        return;
      }

      inFlightRef.current = null;

      // Ours was overwritten before it went out, so the add is not spent and re-fires later.
      if (queuedPrompt !== null) return;

      // Ours has been taken, so this add is spent and the next one can go.
      if (!hasFired) dispatch(markTargetAnalysisFired());

      dispatch(shiftPendingAnalysis());

      return;
    }

    if (facts === null || queuedPrompt !== null) return;

    const timestamp = Date.now();

    inFlightRef.current = { timestamp, schoolName: facts.school.name };
    dispatch(
      setQueuedPrompt({
        label: STRINGS.nav.targetSchoolsPrompt,
        // Named per school, so five adds don't read as the same sentence sent five times.
        chatMessage: STRINGS.nav.targetSchoolChatMessage(facts.school.name),
        prompt: STRINGS.nav.targetSchoolsPrompt,
        promptButtonId: PROMPT_BUTTON_ID,
        timestamp,
      }),
    );
  }, [facts, queuedPrompt, hasFired, dispatch]);

  // Leaving the step unregisters the facts, so a prompt still waiting would go out bare.
  useEffect(
    () => () => {
      const inFlight = inFlightRef.current;

      if (inFlight !== null && isOurPrompt(queuedPromptRef.current, inFlight.timestamp)) {
        dispatch(clearQueuedPrompt());
      }
    },
    [dispatch],
  );
};
